import { errorMessages, fieldLabel } from '../i18n';
import { queryClient } from './query-cache';
import { createApiClient, type components } from '@snaphire/api-client';

export type Account = components['schemas']['AccountResponse'];
export type AuthSession = components['schemas']['AuthSessionResponse'];
type State = { ready: boolean; account: Account | null; error: string | null };
let state: State = { ready: false, account: null, error: null };
let accessToken: string | null = null;
let accessTokenExpiresAt: number | null = null;
let refreshing: Promise<AuthSession> | null = null;
const listeners = new Set<() => void>();
const auth = createApiClient({ credentials: 'include' });

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export function apiError(response: Response, error: unknown): ApiError {
  const value = error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const nested =
    value.error && typeof value.error === 'object'
      ? (value.error as Record<string, unknown>)
      : value;
  const details =
    nested.details && typeof nested.details === 'object'
      ? (nested.details as Record<string, unknown>)
      : {};
  const fieldErrors = nested.fieldErrors || details.fieldErrors;
  const fields = Array.isArray(fieldErrors)
    ? [
        ...new Set(
          fieldErrors
            .map((item) => {
              const issue = item as { field?: string; message?: string };
              const key =
                String(issue.field || '')
                  .split(/[/.]/)
                  .filter(Boolean)
                  .filter((part) => !/^\d+$/.test(part))
                  .at(-1) ||
                issue.message?.match(/required property ['"]([^'"]+)/)?.[1] ||
                '';
              return fieldLabel(key);
            })
            .filter((label) => label !== 'Параметр'),
        ),
      ]
    : [];
  const validationMessage =
    String(nested.code) === 'VALIDATION_FAILED' && fields.length
      ? `Проверьте поля: ${fields.join(', ').toLocaleLowerCase('ru')}`
      : String(nested.code) === 'VALIDATION_FAILED' &&
          /\p{Script=Cyrillic}/u.test(String(nested.message || ''))
        ? String(nested.message)
        : undefined;
  return new ApiError(
    response.status,
    String(nested.code || 'REQUEST_FAILED'),
    validationMessage ||
      errorMessages[String(nested.code)] ||
      (/\p{Script=Cyrillic}/u.test(String(nested.message || ''))
        ? String(nested.message)
        : 'Не удалось выполнить запрос'),
  );
}

export function cookieValue(name: string): string | undefined {
  const cookie = document.cookie.split('; ').find((value) => value.startsWith(`${name}=`));
  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : undefined;
}

function publish(next: State): void {
  state = next;
  listeners.forEach((listener) => listener());
}
export const sessionStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot: () => state,
};
export function token(): string | null {
  return accessToken;
}
export function acceptSession(session: AuthSession): void {
  if (state.account?.id !== session.account.id) queryClient.clear();
  accessToken = session.accessToken;
  const expiresAt = Date.parse(session.accessTokenExpiresAt);
  accessTokenExpiresAt = Number.isFinite(expiresAt) ? expiresAt : null;
  publish({ ready: true, account: session.account, error: null });
}
export function clearSession(): void {
  queryClient.clear();
  accessToken = null;
  accessTokenExpiresAt = null;
  publish({ ready: true, account: null, error: null });
}

export async function refreshSession(): Promise<AuthSession> {
  if (refreshing) return refreshing;
  const refresh = async () => {
    const csrf = cookieValue('__Host-snaphire_csrf');
    if (!csrf) {
      clearSession();
      throw new ApiError(401, 'NO_SESSION', 'Войдите в систему');
    }
    let fingerprint = sessionStorage.getItem('snaphire-session-fingerprint');
    if (!fingerprint) {
      fingerprint = crypto.randomUUID();
      sessionStorage.setItem('snaphire-session-fingerprint', fingerprint);
    }
    const result = await auth.POST('/public/v1/auth/refresh', {
      body: { requestFingerprint: fingerprint },
      params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
      headers: { 'X-CSRF-Token': csrf },
    });
    if (!result.data) {
      clearSession();
      throw apiError(result.response, result.error);
    }
    acceptSession(result.data);
    return result.data;
  };
  const operation =
    typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks.request('snaphire-auth-refresh', refresh)
      : refresh();
  refreshing = Promise.resolve(operation).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export async function ensureSessionFresh(): Promise<void> {
  if (accessToken && accessTokenExpiresAt !== null && accessTokenExpiresAt - Date.now() <= 30_000)
    await refreshSession();
}

export async function restoreSession(): Promise<void> {
  try {
    await refreshSession();
  } catch (error) {
    publish({
      ready: true,
      account: null,
      error:
        error instanceof ApiError && error.status === 401
          ? null
          : error instanceof Error
            ? error.message
            : 'Не удалось подключиться',
    });
  }
}

export async function login(email: string, password: string) {
  const result = await auth.POST('/public/v1/auth/login', { body: { email, password } });
  if (!result.data) throw apiError(result.response, result.error);
  if ('accessToken' in result.data) acceptSession(result.data);
  return result.data;
}

export async function verifyMfa(method: 'EMAIL' | 'TOTP', code: string): Promise<void> {
  const result = await auth.POST('/public/v1/auth/mfa/verify', {
    body: { method, code },
    headers: { 'X-CSRF-Token': cookieValue('__Host-snaphire_mfa_csrf') || '' },
  });
  if (!result.data) throw apiError(result.response, result.error);
  acceptSession(result.data);
}

export async function sendMfaEmail(): Promise<void> {
  const result = await auth.POST('/public/v1/auth/mfa/email/send', {
    headers: { 'X-CSRF-Token': cookieValue('__Host-snaphire_mfa_csrf') || '' },
  });
  if (result.error) throw apiError(result.response, result.error);
}

export async function logout(): Promise<void> {
  const result = await auth.POST('/api/v1/auth/logout', {
    body: {},
    params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...{ 'X-CSRF-Token': cookieValue('__Host-snaphire_csrf') || '' },
    },
  });
  if (result.error) throw apiError(result.response, result.error);
  clearSession();
}

export async function activate(tokenValue: string, password: string): Promise<void> {
  const start = await auth.POST('/public/v1/auth/activation-sessions', {
    body: { token: tokenValue },
  });
  if (start.error) throw apiError(start.response, start.error);
  const result = await auth.POST('/public/v1/auth/activation/complete', {
    body: { password },
    headers: { 'X-CSRF-Token': cookieValue('__Host-snaphire_action_csrf') || '' },
  });
  if (!result.data) throw apiError(result.response, result.error);
  acceptSession(result.data);
}

export async function requestPasswordReset(email: string): Promise<void> {
  const result = await auth.POST('/public/v1/auth/password-reset/request', {
    body: { email },
    params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
  });
  if (result.error) throw apiError(result.response, result.error);
}

export async function resetPassword(tokenValue: string, password: string): Promise<void> {
  const start = await auth.POST('/public/v1/auth/password-reset-sessions', {
    body: { token: tokenValue },
  });
  if (start.error) throw apiError(start.response, start.error);
  const result = await auth.POST('/public/v1/auth/password-reset/complete', {
    body: { password },
    headers: { 'X-CSRF-Token': cookieValue('__Host-snaphire_action_csrf') || '' },
  });
  if (result.error) throw apiError(result.response, result.error);
  clearSession();
}
